'use client';

/**
 * BellIcon (REA-17 → WEB-433)
 *
 * Header bell that surfaces the user's unread notification count.
 *
 * ─── COUNT IS SERVER-AUTHORITATIVE ───────────────────────────────────────────
 * The badge number is DERIVED FROM SERVER STATE ONLY. It comes from
 * `useGetUnreadCountQuery()` (WEB-432) — the SOLE authoritative source of the
 * unread count. This component NEVER increments/decrements a local counter and
 * NEVER reads a count off a WebSocket frame. The previous implementation
 * proxied the count by counting saved-event WS deltas (SAVED +1 / UNSAVED -1);
 * that hack was dishonest and has been removed.
 *
 * Live updates work without this component mounting any subscription:
 *  - `markRead` invalidates the `Notifications` tag, so the badge refetches
 *    after mark-read with no reload.
 *  - The single canonical `userNotifications` WS subscription is mounted ONCE
 *    at the app shell (WEB-435, via `useUserNotifications`); each live frame
 *    invalidates the `Notifications` tag, which refetches this query. Do NOT
 *    mount a second subscription here — that would create a duplicate WS sub.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Behaviour:
 *  - count comes from `getUnreadCount`; zero hides the badge entirely
 *  - count display caps at `99+` for readability
 *  - a brief pulse fires when the refetched count INCREASES (a transient
 *    visual cue only — the number still comes from the server)
 *  - clicking navigates to `/saved` so users can act on the count
 *
 * Component uses the View+Connected split:
 *  - `BellIconView` is pure props (rendered by stories + tests) — UNCHANGED
 *  - `BellIcon` reads the unread count from RTK Query
 */

import * as React from 'react';
import Link from 'next/link';
import { Bell } from 'lucide-react';
import { useGetUnreadCountQuery } from '@loci/api';

const PULSE_DURATION_MS = 1000;
const COUNT_DISPLAY_CAP = 99;

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-(--loci-bell-ring-offset)';

const ICON_BUTTON =
  'relative inline-flex size-11 desktop:size-9 items-center justify-center rounded-full border border-white/[0.14] bg-white/10 text-white transition-colors duration-150 hover:bg-white/15 motion-reduce:transition-none';

// `data-pulsing="true"` is the styling hook callers use to drive the pulse
// animation (Tailwind `data-[pulsing=true]:animate-pulse` or a project-defined
// keyframe). Keeping the trigger as a data attribute keeps the JSX clean and
// lets `motion-reduce` queries opt out without component-level branching.
const PULSE_CLASS =
  'data-[pulsing=true]:animate-pulse motion-reduce:data-[pulsing=true]:animate-none';

/**
 * Single source of truth for the badge cap rule: counts above the cap render as
 * `99+`, everything else as the plain number. Shared by `BellIconView` and
 * `BellIconTrigger` so the cap lives in exactly one place.
 */
export function formatBadgeCount(count: number): string {
  return count > COUNT_DISPLAY_CAP ? `${COUNT_DISPLAY_CAP}+` : String(count);
}

/**
 * Shared badge bubble. Renders nothing when the count is zero (the hidden-at-0
 * rule) and the capped `99+` display otherwise, so both bell variants share one
 * implementation of the badge markup + visibility rule.
 */
function BellBadge({ count }: { count: number }): React.ReactElement | null {
  if (count <= 0) return null;
  return (
    <span
      data-testid="bell-icon-count"
      className="absolute -top-1 -right-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-loci-amber px-1 text-micro font-semibold leading-[18px] text-loci-indigo"
    >
      {formatBadgeCount(count)}
    </span>
  );
}

export interface BellIconViewProps {
  /** Current unread count. Zero hides the badge entirely. */
  count: number;
  /** When true, the button advertises the pulse via `data-pulsing="true"`. */
  isPulsing: boolean;
  /** Override the navigation href (defaults to `/saved`). */
  href?: string;
  className?: string;
}

/**
 * Pure presentational bell icon. Drives only off the props it receives so
 * Storybook + unit tests can pin every visual state.
 */
export function BellIconView({
  count,
  isPulsing,
  href = '/saved',
  className,
}: BellIconViewProps): React.ReactElement {
  const hasCount = count > 0;
  const ariaLabel = hasCount
    ? `Notifications, ${count} unread saved-event update${count === 1 ? '' : 's'}`
    : 'Notifications';

  return (
    <Link
      href={href}
      aria-label={ariaLabel}
      data-pulsing={isPulsing ? 'true' : undefined}
      className={[ICON_BUTTON, FOCUS_RING, PULSE_CLASS, className].filter(Boolean).join(' ')}
    >
      <Bell className="size-5" aria-hidden="true" />
      <BellBadge count={count} />
    </Link>
  );
}

export interface BellIconTriggerProps {
  /** Current unread count. Zero hides the badge entirely. */
  count: number;
  /** When true, the button advertises the pulse via `data-pulsing="true"`. */
  isPulsing: boolean;
  /** True when the hosting popover / sheet is open — wires `aria-expanded`. */
  isOpen?: boolean;
  className?: string;
}

/**
 * Button variant of the bell, used as the trigger for the NotificationCenter
 * popover / sheet (WEB-436). It mirrors `BellIconView` visually (same ring,
 * button chrome, pulse hook, and `99+` badge) but renders a real `<button>`
 * instead of a `<Link>` so Radix can drive `aria-expanded` / focus-restore and
 * the click opens the panel in place rather than navigating to `/saved`.
 *
 * `forwardRef` + spread props are required so Radix's `Trigger asChild` can
 * attach its ref, `onClick`, and `aria-*` to this element.
 */
export const BellIconTrigger = React.forwardRef<HTMLButtonElement, BellIconTriggerProps>(
  function BellIconTrigger({ count, isPulsing, isOpen, className, ...rest }, ref) {
    const hasCount = count > 0;
    const ariaLabel = hasCount
      ? `Notifications, ${count} unread saved-event update${count === 1 ? '' : 's'}`
      : 'Notifications';

    return (
      <button
        ref={ref}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen ?? false}
        data-pulsing={isPulsing ? 'true' : undefined}
        data-testid="notification-bell-trigger"
        className={[ICON_BUTTON, FOCUS_RING, PULSE_CLASS, className].filter(Boolean).join(' ')}
        {...rest}
      >
        <Bell className="size-5" aria-hidden="true" />
        <BellBadge count={count} />
      </button>
    );
  },
);

export interface BellIconProps {
  /** Authenticated user id. `null` hides the bell entirely (signed-out). */
  userId: string | null;
  className?: string;
}

/**
 * Connected bell icon. Reads the unread count from `useGetUnreadCountQuery()`
 * (WEB-432) — the server-authoritative source.
 *
 * The subscription that keeps this count fresh on live frames is mounted ONCE
 * at the app shell (WEB-435); this component intentionally does NOT mount a
 * `useUserNotifications()` subscription to avoid a duplicate WS connection.
 * It simply consumes the query, which the shell's tag invalidation refetches.
 */
export function BellIcon({ userId, className }: BellIconProps): React.ReactElement | null {
  // Skip the query entirely while signed out so we never 401 on the anon
  // surface. The hook is still called unconditionally (stable hook order);
  // `skip` toggles the fetch.
  const isSignedOut = userId === null || userId === '';
  const { data } = useGetUnreadCountQuery(undefined, { skip: isSignedOut });
  const count = data?.count ?? 0;

  const [isPulsing, setIsPulsing] = React.useState(false);
  const prevCountRef = React.useRef(count);
  const pulseTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fire a transient pulse whenever the SERVER count increases. The badge value
  // itself is always `count` from the query — the pulse is a visual cue only and
  // never affects the displayed number.
  React.useEffect(() => {
    const increased = count > prevCountRef.current;
    prevCountRef.current = count;
    if (!increased) return;

    if (pulseTimerRef.current) {
      clearTimeout(pulseTimerRef.current);
    }
    setIsPulsing(true);
    pulseTimerRef.current = setTimeout(() => {
      setIsPulsing(false);
      pulseTimerRef.current = null;
    }, PULSE_DURATION_MS);
  }, [count]);

  // Always cleanup the pulse timer on unmount so we don't drop a stray
  // setState on an unmounted component (and so rapid mounts don't leak).
  React.useEffect(() => {
    return () => {
      if (pulseTimerRef.current) {
        clearTimeout(pulseTimerRef.current);
        pulseTimerRef.current = null;
      }
    };
  }, []);

  // Hide the bell entirely when there's no user — keeps the marketing
  // surface clean and avoids querying an auth-scoped endpoint. Uses the same
  // `isSignedOut` predicate that gates the query, so an empty-string userId
  // (signed-out) hides the bell rather than rendering an empty `count={0}` shell.
  if (isSignedOut) {
    return null;
  }

  return <BellIconView count={count} isPulsing={isPulsing} className={className} />;
}
